import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import {
  AudioPlayer,
  Button,
  Card,
  Icon,
  LevelMeter,
  RecordButton,
  Skeleton,
  Stepper,
  Waveform,
} from "@xinghai/ui";
import { MAX_DURATION_MS } from "../../lib/audio/recorder";
import { useRecorder } from "../../lib/audio/useRecorder";
import { isNoiseChecked } from "../../lib/audio/noiseCheck";
import { buzz } from "../../lib/haptics";
import { studentApi } from "../../lib/api";
import { useAuth } from "../auth/useAuth";
import { Illustration } from "../shared/Illustration";
import { NoiseCheckSheet } from "./NoiseCheckSheet";
import { MicHelp } from "./MicHelp";
import { markOnboarded, needsWarmup, onboardingIndex, onboardingSteps } from "./onboarding";

/** Nothing is uploaded from the warm-up; it only shows how a take feels. */
const PRACTICE = { char: "诗", pinyin: "shī" };

/**
 * docs/UX.md §1.1 steps 3–4: 环境检测 → 试录一条.
 * The student records one character, hears it back, and learns the two-tap
 * rhythm before any real task starts.
 */
export function WarmupPage() {
  const navigate = useNavigate();
  const { me, isLoading } = useAuth();
  const [phase, setPhase] = useState<"mic" | "try">(() => (isNoiseChecked() ? "try" : "mic"));
  const recorder = useRecorder();
  const { status, result, start, stop, reset, errorMessage } = recorder;

  useEffect(() => {
    if (phase === "try" && status === "recording") buzz(12);
  }, [phase, status]);

  const step = phase === "mic" ? "mic" : "warmup";

  const finish = () => {
    markOnboarded();
    void studentApi.finishWarmup().catch(() => undefined);
    void navigate("/s", { replace: true });
  };

  // A student who already recorded, or finished 试录 on another device, never
  // sees the noise check or the live trial recorder.
  if (isLoading && needsWarmup(me?.warmup_required)) {
    return (
      <div className="xz-container xz-stack" aria-busy="true">
        <Skeleton variant="rect" height={120} />
      </div>
    );
  }
  if (!needsWarmup(me?.warmup_required)) return <Navigate to="/s" replace />;

  return (
    <div className="xz-container xz-container--readable xz-stack">
      <Stepper steps={onboardingSteps(step)} current={onboardingIndex(step)} label="开始前的四步" />

      {phase === "mic" ? (
        <>
          <h1 className="gz-type-heading">先看看这里安不安静</h1>
          <Illustration name="guide-quiet" size="lg" />
          <p className="gz-type-reading">
            录音最怕背景噪声。我们听 2 秒你周围的声音，判断这里适不适合录。这段声音不会上传。
          </p>
          <NoiseCheckSheet open onDone={() => setPhase("try")} onSkip={() => setPhase("try")} />
        </>
      ) : (
        <>
          <h1 className="gz-type-heading">试录一条，感受一下</h1>
          <p className="gz-type-reading">
            点一下开始，读出下面这个字，再点一下停止。正式任务里也是这两下，录完会自动提交。
          </p>

          <Illustration
            name="guide-distance"
            size="lg"
            caption="手机离嘴一个手掌远，别挡住嘴"
          />

          <Card padding="lg">
            <section className="xz-material xz-material--single" aria-label="试录的字">
              <span className="xz-material__char" lang="zh-CN">
                {PRACTICE.char}
              </span>
              <span className="xz-material__pinyin">{PRACTICE.pinyin}</span>
            </section>
          </Card>

          {errorMessage ? (
            <MicHelp message={errorMessage} onRetry={() => void start()} />
          ) : !result ? (
            <div className="xz-record-page__control">
              <RecordButton
                state={
                  status === "recording"
                    ? "recording"
                    : status === "requesting"
                      ? "disabled"
                      : "idle"
                }
                onToggle={() => {
                  buzz(12);
                  if (status === "recording") void stop();
                  else void start();
                }}
                elapsedMs={recorder.elapsedMs}
                maxMs={MAX_DURATION_MS}
                size={112}
              />
              <LevelMeter
                rms={recorder.level.rms}
                peak={recorder.level.peak}
                clipped={recorder.level.clipped}
              />
              <p className="gz-caption">
                {status === "recording" ? "读完再点一下停止。" : "准备好了就点一下。"}
              </p>
            </div>
          ) : (
            <div className="xz-record-page__preview xz-stack">
              <p className="gz-notice">
                <Icon id="ui-check-circle" size={20} />
                这就是你录出来的声音。每次任务都是这样：点一下、读、再点一下。
              </p>
              <Waveform
                peaks={recorder.peaks}
                durationSec={result.durationMs / 1000}
                height={72}
                label="试录波形"
              />
              {recorder.previewUrl && (
                <AudioPlayer src={recorder.previewUrl} hideRate label="试听试录" />
              )}
              <div className="xz-record-page__actions">
                <Button variant="secondary" size="lg" onClick={reset}>
                  <Icon id="ui-refresh" size={20} /> 再试一次
                </Button>
                <Button variant="primary" size="lg" block onClick={finish}>
                  开始今天的任务
                </Button>
              </div>
            </div>
          )}

          <Button variant="ghost" onClick={finish}>
            跳过，直接开始
          </Button>
        </>
      )}
    </div>
  );
}
